import { Menu as MenuIcon, Search, X } from 'lucide-react';
import { type ComponentType, type ReactNode, useRef } from 'react';
import { useShell } from '@/components/layout/shell';
import { Button } from '@/components/ui/button';
import { Scrim, useEscapeToClose, useReturnFocus } from '@/components/ui/overlay';
import { useWideLayout } from '@/hooks/use-media-query';
import { useBodyScrollLock } from '@/lib/scroll-lock';
import { cn } from '@/lib/utils';

/**
 * A view is a sticky header over a scrolling body, and the header is the only
 * chrome. There is no global top bar: the rail on the left holds identity and
 * navigation, so this line holds the name of what you are looking at, one quiet
 * clause of context, and the actions that belong to it.
 *
 * It got 20px taller and grew a mark in this pass, for a reason worth writing
 * down. The old header was a 21px title and a 13px clause sharing one baseline
 * on a 56px bar, with the page's primary verb rendered as a 28px outlined button
 * pinned to the far right. Every page in the console opened with the same
 * hairline-grey line, and the one thing a person had come to do -- file a job,
 * enroll a device -- was the quietest control on it. A header is the first thing
 * read and it was carrying no weight at all.
 *
 * Now: a brand-tinted mark, the title on its own line, the clause under it, and
 * the primary verb as a filled pill in the gradient. The mark is what makes the
 * three-line block cohere; without it the title floats at the left edge with a
 * ragged gap under it.
 *
 * On a phone the same line grows the two controls the rail was holding -- the nav
 * sheet and the palette -- because a person approving a gate from a phone needs
 * to get between views without a 248px rail eating the screen.
 */
export function ViewHeader({
  title,
  sub,
  actions,
  /**
   * The view's mark, in a brand-tinted well at the left of the header.
   *
   * The same icon the rail draws for this destination, so the eye going from a
   * nav row to the page it opened lands on the shape it just clicked.
   */
  icon: Icon,
  /** A CSS colour for the mark's well. Defaults to the accent. */
  tone,
  /**
   * The measure of the page under this header, so the two line up.
   *
   * A header pinned to the window's left edge over a body centred in a narrower
   * column reads as two pages stacked: the view's name sits above nothing, and
   * the first heading of the content starts somewhere else entirely. Pass the
   * same value the page's `Page` uses.
   */
  width = '1560px',
}: {
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  /** Lucide's icons and Claude's own mark both satisfy this. */
  icon?: ComponentType<{ className?: string }>;
  tone?: string;
  width?: string;
}) {
  const { setNavOpen, openPalette } = useShell();
  const hue = tone ?? 'var(--dy-accent)';
  return (
    <header
      className={cn(
        'border-line/70 sticky top-0 z-20 flex h-16 shrink-0 items-center border-b px-3',
        'bg-bar/72 backdrop-blur-xl backdrop-saturate-150 sm:px-6',
      )}
    >
      <div className="mx-auto flex w-full items-center gap-3" style={{ maxWidth: width }}>
        <Button
          variant="ghost"
          size="icon-sm"
          className="lg:hidden"
          onClick={() => setNavOpen(true)}
          aria-label="Open navigation"
        >
          <MenuIcon />
        </Button>

        {Icon ? (
          <span
            className="hidden size-9 shrink-0 place-items-center rounded-xl sm:grid"
            style={{
              background: `color-mix(in srgb, ${hue} 13%, transparent)`,
              color: hue,
            }}
            aria-hidden
          >
            <Icon className="size-[18px]" />
          </span>
        ) : null}

        <div className="min-w-0 flex-1">
          <h1 className="truncate text-xl leading-tight font-semibold tracking-[-0.019em]">
            {title}
          </h1>
          {sub ? (
            <div className="text-fg-3 mt-0.5 hidden truncate text-xs sm:block">{sub}</div>
          ) : null}
        </div>

        {/* `min-w-0` and no `shrink-0`: the steps header carries two filter menus
            next to the phone's nav and palette buttons, and a wrapper that refuses
            to shrink pushed the row past a 390px screen. The menus inside truncate
            instead. */}
        <div className="ml-auto flex min-w-0 shrink items-center gap-1.5">
          {actions}
          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            onClick={openPalette}
            aria-label="Open the command palette"
          >
            <Search />
          </Button>
        </div>
      </div>
    </header>
  );
}

/** The body under a view header. Its measure must match the header's `width`. */
export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('mx-auto w-full max-w-[1560px] p-3 pt-6 sm:px-6 sm:pb-10', className)}>
      {children}
    </div>
  );
}

/**
 * A list beside a detail pane. The pane is a route, not component state, so a
 * plan waiting for approval is a link somebody can send.
 *
 * There is only width for both from `xl` up. Below it the pane used to stack
 * under every group on the page, which on a phone meant tapping a task scrolled
 * nothing, changed nothing visible, and read as a dead row -- the detail was
 * five panels further down. So below `xl` a selection opens a sheet over the
 * list: the same element, the same route, one instance of the detail either way.
 * It scrims the page, locks the scroll behind it, takes the focus and gives it
 * back, and closes on Escape, on the scrim, and on its own button -- all of
 * which land back on the list route, because the selection *is* the route.
 *
 * `overlay` is the second shape a caller can ask for (issue #62). The default
 * above reserves a 400px column from `xl` up whether or not there is anything
 * to put in it -- right for a schedule, whose list does not mind losing a
 * fifth of its width and whose empty pane is one skinny "select one" line. It
 * is wrong for the task board: an empty pane is a hole that explains itself,
 * and a selected task turned the four lanes into a strip at the exact moment
 * a person was comparing one against the rest of its lane. `overlay` never
 * reserves that column -- the list always has the full width -- and a
 * selection floats a panel over its right edge instead of asking it to
 * shrink. Below `xl` the two shapes agree: a sheet is already an overlay.
 */
export function SplitPane({
  list,
  peek,
  /** True when nothing is selected: below `xl` the pane is simply not drawn. */
  peekEmpty = false,
  /** Back to the list route. Without it the sheet has no way out but Back. */
  onClose,
  /** The list never gives up width for the pane; a selection floats over it instead. */
  overlay = false,
}: {
  list: ReactNode;
  peek: ReactNode;
  peekEmpty?: boolean;
  onClose?: () => void;
  overlay?: boolean;
}) {
  const wide = useWideLayout();
  const { paletteOpen, navOpen } = useShell();
  const hasPeek = !peekEmpty;
  // No `onClose` means no way out of a modal, so a caller that has not given one
  // keeps the stacked pane rather than getting a sheet nobody can dismiss.
  const canClose = Boolean(onClose);
  // Below `xl` both shapes are the same sheet: a bottom modal that scrims and
  // locks the page behind it.
  const mobileSheet = hasPeek && !wide && canClose;
  // From `xl` up, an overlay caller still floats the pane rather than falling
  // back to the static column -- that column is the squeeze this exists to
  // avoid. A non-overlay caller keeps the column, unchanged.
  const sidePanel = overlay && hasPeek && wide && canClose;
  // Either shape is a floating pane a person can dismiss with Escape or its
  // own button; only the mobile sheet is modal (it covers the list, so the
  // list is `inert` under it and the scrim gives it a second way to close).
  const floating = mobileSheet || sidePanel;
  const closeButton = useRef<HTMLButtonElement>(null);
  // The same element is a dialog on a phone and a column on a desk, so the role
  // travels as a group rather than as three ternaries on one tag.
  const modal = mobileSheet
    ? ({ role: 'dialog', 'aria-modal': true, 'aria-label': 'Detail' } as const)
    : null;

  // The list behind must not scroll under the sheet. Counted, because the palette
  // is often the thing that opened this and is still up: see `scroll-lock.ts`.
  // A side panel does not cover the list, so nothing needs locking for it.
  useBodyScrollLock(mobileSheet);

  /**
   * Escape closes the floating pane, but only when Escape is not already
   * spoken for.
   *
   * `useEscapeToClose` is a window listener, so it hears every Escape on the
   * page including the ones meant for something else inside the pane. The
   * things inside stop the key themselves (`AskBox`, `Menu`, `Combobox`); the
   * two that cannot, because they are drawn over the pane rather than inside
   * it, are the palette and the nav sheet, which is what `suppressed` is for.
   * Shared with the verb popovers and the job composer (issue #89), so the
   * console has one Escape-to-close rather than a copy per floating panel.
   */
  useEscapeToClose(floating, () => onClose?.(), paletteOpen || navOpen);

  // Focus moves into the sheet and returns to the row that opened it, as long as
  // that row still exists -- a list that re-sorted while the sheet was open may
  // not have it any more, and focusing a detached node moves focus to nowhere.
  // Only the modal sheet does this: a side panel is not modal (the board keeps
  // its own roving focus while one floats over it), so stealing focus into it
  // the moment a task is picked would break arrow-key navigation on the very
  // row a person just selected.
  useReturnFocus(mobileSheet, closeButton);

  return (
    <div
      className={cn(
        'grid gap-[var(--dy-gap)]',
        !overlay && 'xl:grid-cols-[minmax(0,1fr)_400px]',
        // The side panel positions itself against this box, not the viewport,
        // so it lines up with the board's own right edge on a wide monitor
        // where the page's own max-width leaves margin outside it.
        overlay && 'relative',
      )}
    >
      {/* `inert` while the modal sheet is up: `aria-modal` is a claim to a
          screen reader and nothing at all to the Tab key, and a list of forty
          rows behind a sheet is forty tab stops out of the dialog. A side
          panel is not modal, so the list beside it stays exactly as usable. */}
      <div className="dy-stack min-w-0" inert={mobileSheet}>
        {list}
      </div>

      {mobileSheet ? (
        <Scrim onClose={() => onClose?.()} label="Close the detail" className="fixed z-40" />
      ) : null}

      {/* An overlay caller with nothing selected draws none of this: no
          column, no placeholder, nothing at all on the right (issue #62). */}
      {overlay && !hasPeek ? null : (
        <div
          {...modal}
          className={cn(
            'bg-card dy-scroll overflow-hidden rounded-lg border',
            mobileSheet &&
              'dy-sheet-in fixed inset-x-0 bottom-0 z-50 max-h-[82vh] overflow-y-auto rounded-b-none',
            sidePanel &&
              'dy-pane-in absolute inset-y-0 right-0 z-40 w-[400px] overflow-y-auto rounded-none border-l shadow-2xl',
            !overlay &&
              !mobileSheet &&
              'h-fit xl:sticky xl:top-[4rem] xl:max-h-[calc(100vh-4.75rem)] xl:overflow-y-auto',
            !overlay && peekEmpty && 'hidden xl:block',
          )}
        >
          {floating ? (
            <div className="bg-card/95 sticky top-0 z-[1] flex h-9 items-center justify-end border-b px-1.5 backdrop-blur-md">
              <Button
                ref={closeButton}
                variant="ghost"
                size="icon-sm"
                onClick={onClose}
                aria-label="Close the detail"
              >
                <X />
              </Button>
            </div>
          ) : null}
          {peek}
        </div>
      )}
    </div>
  );
}
